/**
 * Data Service
 * Handles all data fetching operations for collegiate NFL fantasy data
 */

import { getClassSummaryUrl, getSeasonDataUrl, STORAGE_BASE_URL } from "../config/api";
import { CURRENT_YEAR, FIRST_YEAR } from "../constants/app";
import type { ClassSummaryFile } from "../types/classSummary";
import type { DraftClassRange } from "../utils/draftClassFilter";
import type { DraftRoundWorkerParams } from "../utils/draftRoundFilter";

/** Player filters applied to the season data in the worker. */
export interface DataFilterValues {
  draftClass: DraftClassRange | null;
  draftRound: DraftRoundWorkerParams | null;
  /** athleteInfo.pos values to keep (see toPositionWorkerParams). */
  positions: string[] | null;
}

interface Worker {
  applyDataFilters?: (params: {
    data: any;
    statFilter: string;
    classFrom?: number;
    classTo?: number;
    draft?: DraftRoundWorkerParams;
    positions?: string[];
  }) => Promise<any>;
}

/** Revalidate cached Storage JSON at most this often (matches Hosting SSR Cache-Control). */
const CACHE_REVALIDATE_MS = 5 * 60 * 1000;

type JsonCacheEntry<T> = {
  data: T;
  /** Storage object md5Hash when available (Firebase Storage metadata). */
  md5Hash: string | null;
  /** Storage object updated timestamp when available. */
  updated: string | null;
  /** Last successful metadata (or TTL) check. */
  checkedAt: number;
  /** In-flight reload so concurrent callers share one fetch. */
  pending?: Promise<T>;
};

type StorageObjectMeta = {
  md5Hash: string | null;
  updated: string | null;
};

/**
 * Convert a Firebase Storage media URL (.../o/path%2Ffile.json?alt=media) into the
 * metadata API URL (same path, no alt=media). Returns null for non-Storage URLs
 * (e.g. local /summary/ in development).
 */
export const getStorageMetadataUrl = (mediaUrl: string): string | null => {
  if (!mediaUrl || !mediaUrl.startsWith(STORAGE_BASE_URL)) return null;
  try {
    const parsed = new URL(mediaUrl);
    if (!parsed.pathname.includes('/o/')) return null;
    parsed.searchParams.delete('alt');
    return parsed.toString();
  } catch {
    return null;
  }
};

const fetchStorageMetadata = async (mediaUrl: string): Promise<StorageObjectMeta | null> => {
  const metaUrl = getStorageMetadataUrl(mediaUrl);
  if (!metaUrl) return null;
  try {
    const response = await fetch(metaUrl);
    if (!response.ok) return null;
    const meta = await response.json();
    return {
      md5Hash: typeof meta.md5Hash === 'string' ? meta.md5Hash : null,
      updated: typeof meta.updated === 'string' ? meta.updated : null,
    };
  } catch {
    return null;
  }
};

const fetchJson = async <T,>(url: string): Promise<T> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Cache Storage JSON by URL. Keeps the payload, but revalidates against Storage
 * object md5Hash/updated at most every CACHE_REVALIDATE_MS. Falls back to a plain
 * TTL when metadata is unavailable (local /summary/ paths).
 */
const createJsonCache = <T,>() => {
  const cache = new Map<string, JsonCacheEntry<T>>();

  const clear = () => cache.clear();

  const load = async (url: string): Promise<T> => {
    const now = Date.now();
    const existing = cache.get(url);

    if (existing?.pending) return existing.pending;

    if (existing && now - existing.checkedAt < CACHE_REVALIDATE_MS) {
      return existing.data;
    }

    const pending = (async () => {
      if (!existing) {
        const [data, meta] = await Promise.all([
          fetchJson<T>(url),
          fetchStorageMetadata(url),
        ]);
        cache.set(url, {
          data,
          md5Hash: meta?.md5Hash ?? null,
          updated: meta?.updated ?? null,
          checkedAt: Date.now(),
        });
        return data;
      }

      const meta = await fetchStorageMetadata(url);
      // No metadata API (local URL): TTL-only refresh.
      if (!meta) {
        const data = await fetchJson<T>(url);
        cache.set(url, {
          data,
          md5Hash: null,
          updated: null,
          checkedAt: Date.now(),
        });
        return data;
      }

      const unchanged =
        (meta.md5Hash && existing.md5Hash && meta.md5Hash === existing.md5Hash) ||
        (meta.updated && existing.updated && meta.updated === existing.updated);

      if (unchanged) {
        existing.checkedAt = Date.now();
        existing.md5Hash = meta.md5Hash;
        existing.updated = meta.updated;
        delete existing.pending;
        return existing.data;
      }

      const data = await fetchJson<T>(url);
      cache.set(url, {
        data,
        md5Hash: meta.md5Hash,
        updated: meta.updated,
        checkedAt: Date.now(),
      });
      return data;
    })();

    if (existing) {
      existing.pending = pending;
    } else {
      // Placeholder so concurrent first loads share the same promise.
      cache.set(url, {
        data: undefined as unknown as T,
        md5Hash: null,
        updated: null,
        checkedAt: 0,
        pending,
      });
    }

    try {
      return await pending;
    } catch (err) {
      const current = cache.get(url);
      if (current?.pending === pending) {
        if (current.checkedAt === 0 && current.data === undefined) {
          cache.delete(url);
        } else {
          delete current.pending;
        }
      }
      throw err;
    } finally {
      const current = cache.get(url);
      if (current?.pending === pending) delete current.pending;
    }
  };

  return { load, clear, _cache: cache };
};

const seasonCache = createJsonCache<any>();
const classSummaryCache = createJsonCache<ClassSummaryFile>();

export const clearSeasonDataCache = (): void => {
  seasonCache.clear();
};

export const clearClassSummaryCache = (): void => {
  classSummaryCache.clear();
};

/**
 * Fetches collegiate fantasy data for a given year
 * @param year - The year to fetch data for
 * @param statFilter - Optional stat filter to apply
 * @param worker - Worker instance for data processing (optional)
 * @param filters - Optional player filters (class range, draft rounds, positions); other players are removed
 * @returns The fetched and filtered data
 */
export const fetchCollegiateFantasyData = async (
  year: number,
  statFilter: string = "",
  worker: Worker | null = null,
  filters: DataFilterValues | null = null
): Promise<any> => {
  try {
    const isCurrentSeason = year === CURRENT_YEAR;
    const url = getSeasonDataUrl(year, isCurrentSeason);
    const data = await seasonCache.load(url);
    const draftClass = filters?.draftClass ?? null;
    const draftRound = filters?.draftRound ?? null;
    const positions = filters?.positions?.length ? filters.positions : null;

    if (worker && worker.applyDataFilters && (statFilter || draftClass || draftRound || positions)) {
      return await worker.applyDataFilters({
        data,
        statFilter,
        ...(draftClass ? { classFrom: draftClass.from, classTo: draftClass.to } : {}),
        ...(draftRound ? { draft: draftRound } : {}),
        ...(positions ? { positions } : {}),
      });
    }

    return data;
  } catch (error) {
    console.error("Error fetching collegiate fantasy data:", error);
    throw error;
  }
};

export const fetchClassSummary = async (
  fromYear: number = FIRST_YEAR,
  toYear: number = CURRENT_YEAR
): Promise<ClassSummaryFile> => {
  const url = getClassSummaryUrl(fromYear, toYear);
  return classSummaryCache.load(url);
};

/** Test helpers */
export const __testing = {
  CACHE_REVALIDATE_MS,
  getStorageMetadataUrl,
};
